import {
    Button,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    Skeleton,
    cn,
} from '@repo/shadcn';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, X } from 'lucide-react';
import {
    type KeyboardEvent,
    Ref,
    forwardRef,
    useCallback,
    useRef,
    useState,
} from 'react';

export type Option = Record<'value' | 'label', string> &
    Record<string, string | number>;

type AutoCompleteInputProps = {
    options: Option[];
    emptyMessage: string;
    value?: Option;
    onValueChange?: (value: Option) => void;
    isLoading?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onInputChange?: (value: string) => void;
    renderOption?: (option: Option) => React.ReactNode;
    showSuggestionsOnFocus?: boolean;
    clearOnBlur?: boolean;
};

export const AutoCompleteInput = forwardRef(
    (
        {
            options,
            placeholder,
            emptyMessage,
            value,
            onValueChange,
            disabled,
            isLoading = false,
            onInputChange,
            renderOption = undefined,
            showSuggestionsOnFocus = false,
            clearOnBlur = true,
        }: AutoCompleteInputProps,
        buttonRef: Ref<HTMLButtonElement>,
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);

        const [isOpen, setOpen] = useState(false);
        const [selected, setSelected] = useState<Option>(value as Option);
        const [inputValue, setInputValue] = useState<string>(
            value?.label || '',
        );

        const handleKeyDown = useCallback(
            (event: KeyboardEvent<HTMLDivElement>) => {
                const input = inputRef.current;
                if (!input) {
                    return;
                }

                // Keep the options displayed when the user is typing
                if (!isOpen) {
                    setOpen(true);
                }

                // This is not a default behaviour of the <input /> field
                if (event.key === 'Enter' && input.value !== '') {
                    const optionToSelect = options.find(
                        (option) => option.label === input.value,
                    );
                    if (optionToSelect) {
                        setSelected(optionToSelect);
                        onValueChange?.(optionToSelect);
                    }
                }

                if (event.key === 'Escape') {
                    input.blur();
                }
            },
            [isOpen, options, onValueChange],
        );

        const handleBlur = useCallback(() => {
            setOpen(false);
            if (!clearOnBlur) {
                return;
            }
            setInputValue(selected?.label);
        }, [selected]);

        const handleSelectOption = useCallback(
            (selectedOption: Option) => {
                setInputValue(selectedOption.label);

                setSelected(selectedOption);
                onValueChange?.(selectedOption);

                // This is a hack to prevent the input from being focused after the user selects an option
                // We can call this hack: "The next tick"
                setTimeout(() => {
                    inputRef?.current?.blur();
                }, 0);
            },
            [onValueChange],
        );

        const clearValue = () => {
            // if (!clearOnBlur) {
            //     return;
            // }

            setSelected({
                value: '',
                label: '',
            });
            setInputValue('');
            onValueChange?.({ value: '', label: '' });
            inputRef?.current?.focus();
        };

        const renderCloseButton = () => {
            if (!selected) return null;

            if (disabled) return null;

            if (!inputValue || inputValue === '') return null;

            return (
                <Button
                    onClick={clearValue}
                    type="button"
                    ref={buttonRef}
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 size-6 -translate-y-1/2 p-1"
                    disabled={disabled}
                >
                    <X />
                </Button>
            );
        };

        const shouldShowList = inputValue?.length > 0 || showSuggestionsOnFocus;

        return (
            <CommandPrimitive onKeyDown={handleKeyDown}>
                <div className="relative grow rounded-lg border-[1px] focus-visible:ring-1">
                    <CommandInput
                        ref={inputRef}
                        value={inputValue}
                        onValueChange={(value) => {
                            setInputValue(value);
                            onInputChange?.(value);
                        }}
                        onBlur={handleBlur}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder}
                        disabled={disabled}
                        className="h-10 w-full py-1.5 text-base"
                    />
                    {renderCloseButton()}
                </div>
                <div className="relative">
                    <div
                        className={cn(
                            'absolute top-0 z-10 w-full rounded-xl bg-white outline-none animate-in fade-in-0 zoom-in-95',
                            isOpen && shouldShowList ? 'block' : 'hidden',
                        )}
                    >
                        <CommandList className="rounded-lg ring-1 ring-slate-200">
                            {isLoading ? (
                                <CommandPrimitive.Loading>
                                    <div className="p-1">
                                        <Skeleton className="h-8 w-full" />
                                    </div>
                                </CommandPrimitive.Loading>
                            ) : null}
                            {options.length > 0 && !isLoading ? (
                                <CommandGroup>
                                    {options.map((option) => {
                                        const isSelected =
                                            selected?.value === option.value;
                                        return (
                                            <CommandItem
                                                key={option.value}
                                                value={option.label}
                                                onMouseDown={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                }}
                                                onSelect={() =>
                                                    handleSelectOption(option)
                                                }
                                                className={cn(
                                                    'flex w-full items-center gap-2',
                                                    !isSelected ? 'pl-8' : null,
                                                )}
                                            >
                                                {isSelected ? (
                                                    <Check className="w-4" />
                                                ) : null}
                                                {renderOption
                                                    ? renderOption(option)
                                                    : option.label}
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            ) : null}
                            {!isLoading ? (
                                <CommandPrimitive.Empty className="select-none rounded-sm px-2 py-3 text-center text-sm">
                                    {emptyMessage}
                                </CommandPrimitive.Empty>
                            ) : null}
                        </CommandList>
                    </div>
                </div>
            </CommandPrimitive>
        );
    },
);
